import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { UserDetails, UserProfile, UserLastLoginResponse, UserRoleResponse } from '../interfaces/UserProfile';
import { environment } from '../../environment/environment';
import { ROLE_MAPPINGS, ROLE_TYPE } from '../constants/constants';

export interface MetricsFilterOptions {
  countries: string[];
  departments: string[];
}

export interface MetricsOverviewFilters {
  startDate: string;
  endDate: string;
  country: string;
  department: string;
}

export interface MetricsUserGroup {
  name: string;
  count: number;
  kind: 'value' | 'unknown' | 'others';
}

export interface MetricsOverview {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  totalUsers: number;
  previousTotalUsers: number;
  percentageChange: number | null;
  usersByCountry: MetricsUserGroup[];
  usersByDepartment: MetricsUserGroup[];
  totalExecutionRequests: number;
  previousTotalExecutionRequests: number;
  executionRequestsPercentageChange: number | null;
  requestsByCountry: MetricsUserGroup[];
  requestsByDepartment: MetricsUserGroup[];
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  userProfilePicture$: Observable<string>;
  userInfo$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  baseUrl = environment.backendApiConfiguration.api_url;
  private rawUserRoles$?: Observable<string[]>;
  constructor(private http: HttpClient) {
    this.userProfilePicture$ = this.http
      .get('https://graph.microsoft.com/v1.0/me/photos/48x48/$value', {
        responseType: 'blob',
      })
      .pipe(
        mergeMap(async (image) => {
          const imageUrl = await imageDataUrlFromBlob(image);
          return `url('${imageUrl}')`;
        })
      );
  }
  
  getRawUserRoles(): Observable<string[]> {
    if (!this.rawUserRoles$) {
      this.rawUserRoles$ = this.http
        .get<(UserRoleResponse | null)[]>(`${this.baseUrl}role`)
        .pipe(
          map((roles = []) => roles
            .map(role => role?.displayName?.toLowerCase())
            .filter((role): role is string => !!role)),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.rawUserRoles$;
  }

  getUserRoles(): Observable<string[]> {
    return this.getRawUserRoles().pipe(
      map(roleNames => ROLE_MAPPINGS
        .filter(mapping => mapping.sourceRoles.some(role => roleNames.includes(role)))
        .map(mapping => mapping.userType))
    );
  }

  getApplicationStatisticsAccess(): Observable<boolean> {
    return this.getRawUserRoles().pipe(
      map(roles => roles.includes(ROLE_TYPE.STANDARD_DEV))
    );
  }

  getMetricsFilterOptions(): Observable<MetricsFilterOptions> {
    return this.http.get<MetricsFilterOptions>(`${this.baseUrl}metrics/filter-options`).pipe(
      map(options => {
        if (!options || !Array.isArray(options.countries) || !Array.isArray(options.departments)
          || !options.countries.every(country => typeof country === 'string')
          || !options.departments.every(department => typeof department === 'string')) {
          throw new Error('Invalid metrics filter-options response');
        }
        return options;
      })
    );
  }

  getMetricsOverview(filters: MetricsOverviewFilters): Observable<MetricsOverview> {
    let params = new HttpParams().set('startDate', filters.startDate).set('endDate', filters.endDate);
    if (filters.country) params = params.set('country', filters.country);
    if (filters.department) params = params.set('department', filters.department);
    return this.http.get<MetricsOverview>(`${this.baseUrl}metrics/overview`, { params }).pipe(
      map(overview => {
        const validGroups = (groups: MetricsUserGroup[]) => Array.isArray(groups) && groups.every(group =>
          group && typeof group.name === 'string' && Number.isSafeInteger(group.count) && group.count >= 0
          && ['value', 'unknown', 'others'].includes(group.kind));
        if (!overview || !Number.isSafeInteger(overview.totalUsers) || overview.totalUsers < 0
          || !Number.isSafeInteger(overview.previousTotalUsers) || overview.previousTotalUsers < 0
          || !(overview.percentageChange === null || (typeof overview.percentageChange === 'number' && Number.isFinite(overview.percentageChange)))
          || ![overview.startDate, overview.endDate, overview.previousStartDate, overview.previousEndDate]
            .every(date => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date))
          || !validGroups(overview.usersByCountry) || !validGroups(overview.usersByDepartment)
          || overview.usersByCountry.reduce((sum, group) => sum + group.count, 0) !== overview.totalUsers
          || overview.usersByDepartment.reduce((sum, group) => sum + group.count, 0) !== overview.totalUsers) {
          throw new Error('Invalid metrics overview response');
        }
        if (!Number.isSafeInteger(overview.totalExecutionRequests) || overview.totalExecutionRequests < 0
        || !Number.isSafeInteger(overview.previousTotalExecutionRequests) || overview.previousTotalExecutionRequests < 0
        || !(overview.executionRequestsPercentageChange === null
          || (typeof overview.executionRequestsPercentageChange === 'number' && Number.isFinite(overview.executionRequestsPercentageChange)))
        || !validGroups(overview.requestsByCountry) || !validGroups(overview.requestsByDepartment)
        || overview.requestsByCountry.reduce((sum, group) => sum + group.count, 0) !== overview.totalExecutionRequests
        || overview.requestsByDepartment.reduce((sum, group) => sum + group.count, 0) !== overview.totalExecutionRequests) {
        throw new Error('Invalid metrics execution overview response');
        }
        return overview;
      })
    );
  }

  getUserProfile(): Observable<UserProfile> {
    return this.http
      .get<UserProfile>(
        'https://graph.microsoft.com/v1.0/me?$select=mail,givenName,displayName,surname,id,country,department'
      )
      .pipe(tap(() => this.userInfo$.next(true)));
  }

  saveUserDetails(payload: UserDetails): Observable<any> {
    return this.http.post(`${this.baseUrl}metrics/userdetails`, payload);
  }

  saveLastLogin(lastLoginDateTime: string): Observable<any> {
    return this.http.post(`${this.baseUrl}users/saveLastLogin`, { lastLoginDateTime });
  }

  getLastLogin(): Observable<UserLastLoginResponse> {
    return this.http.get<UserLastLoginResponse>(`${this.baseUrl}users/getLastLogin`);
  }
}

function imageDataUrlFromBlob(image: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        resolve(reader.result as string);
      },
      false
    );

    reader.readAsDataURL(image);
  });
}
